import { ReactNode, useCallback, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { Url } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
} from '@rossum/ui/material';
import { gridClasses } from '@rossum/ui/x-data-grid-pro';
import { UPLOAD_HASH } from '../../../constants/values';
import { WorkspaceWithQueues } from '../../queues/select-queue/types';
import { useUploadDocuments } from '../hooks/useUploadDocument';
import { useUploadEnabled } from '../hooks/useUploadEnabled';
import { useUploadEvents } from '../hooks/useUploadEvents';
import { DragOverlay } from './DragOverlay';
import {
  absoluteMaxSizeStyles,
  AdditionalUploadValues,
  createFileSignature,
  findQueueInWorkspace,
  UploadDocumentsPayload,
} from './helpers';
import { UploadDialog } from './UploadDialog';
import { ValuesFooter } from './ValuesFooter';

type UploadProps = {
  children:
    | ReactNode
    | (({
        onUpload,
      }: {
        onUpload: (files: FileList | null) => void;
      }) => ReactNode);
  activeQueueUrl?: Queue['url'];
  canUploadToQueue?: boolean;
  workspacesWithQueues?: WorkspaceWithQueues[];

  pageTitle?: string;
  onUploadDialogClose?: () => void;
};

export const Upload = ({
  children,
  activeQueueUrl,
  canUploadToQueue,
  workspacesWithQueues,

  pageTitle,
  onUploadDialogClose,
}: UploadProps) => {
  const history = useHistory();
  const intl = useIntl();

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const isOnQueueLevel = !!activeQueueUrl;

  const [selectedQueueUrl, setSelectedQueueUrl] = useState<Url | undefined>(
    activeQueueUrl
  );

  const selectedQueue = useMemo(
    () =>
      workspacesWithQueues && selectedQueueUrl
        ? findQueueInWorkspace(workspacesWithQueues, selectedQueueUrl)
        : undefined,
    [selectedQueueUrl, workspacesWithQueues]
  );

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [hasFormErrors, setHasFormErrors] = useState<boolean>(false);

  const {
    uploadDocuments,
    isLoading: isUploading,
    cancelUpload,
  } = useUploadDocuments();

  const uploadEnabled = useUploadEnabled(true);

  const acceptedMimeTypes = selectedQueue?.settings.acceptedMimeTypes || [];

  const commonInputProps = {
    type: 'file',
    accept: acceptedMimeTypes.join(),
    multiple: true,
  };

  const isUploadDialogOpen = history.location.hash === UPLOAD_HASH;

  const closeUploadDialog = useCallback(() => {
    history.replace({ ...history.location, hash: '' });

    if (!isOnQueueLevel) setSelectedQueueUrl(undefined);

    setSelectedQueueUrl(activeQueueUrl);

    cancelUpload();
    setSelectedFiles([]);
  }, [history, isOnQueueLevel, activeQueueUrl, cancelUpload]);

  const showUploadDialog = useCallback(() => {
    history.replace({ ...history.location, hash: UPLOAD_HASH });
  }, [history]);

  const onDocumentSettled = (signature: string) =>
    setSelectedFiles(prevState => {
      const filteredPrevState = prevState.filter(
        f => createFileSignature(f) !== signature
      );

      // this side effect is ugly but it's the only way what we found how to close the dialog automatically on the last file upload
      if (filteredPrevState.length === 0) {
        if (onUploadDialogClose) onUploadDialogClose();
        closeUploadDialog();
      }

      return filteredPrevState;
    });

  const filterFileFromState = (signature: string) =>
    setSelectedFiles(prevState =>
      prevState.filter(f => createFileSignature(f) !== signature)
    );

  const startUpload = ({
    payload,
    queue,
  }: {
    payload: UploadDocumentsPayload;
    queue: Queue;
  }) => {
    uploadDocuments({
      payload,
      queue,
      onEverySuccess: onDocumentSettled,
      onEveryError: onDocumentSettled,
    });
  };

  // This function handles the cases when;
  // 1. The user drags & drops onto the dashboard.
  // 2. The user clicks on upload button, clicks on choose files and selects files.
  // 3. The user clicks on upload button, and drags & drops files into the upload area.
  // 4. The trial user tries to upload, we pause the upload and we show a warning.
  const handleOnChange = useCallback(
    (fileList: FileList | null) => {
      const fileArray = fileList?.length ? Array.from(fileList) : [];

      if (fileArray.length > 0) {
        setSelectedFiles(prev => [...prev, ...fileArray]);
        if (selectedFiles.length === 0) {
          showUploadDialog();
        }
      }
    },
    [selectedFiles, showUploadDialog]
  );

  const onUploadConfirm = ({ values, metadata }: AdditionalUploadValues) => {
    if (selectedQueue) {
      const payload =
        selectedFiles.map(
          file =>
            ({
              file,
              signature: createFileSignature(file),
              name: file.name,
              queueUrl: selectedQueue.url,
              values,
              metadata,
            }) as const
        ) ?? [];

      const deduplicated = R.uniqueBy(payload, f => f.signature);

      if (deduplicated.length) {
        startUpload({
          payload: deduplicated,
          queue: selectedQueue,
        });
      }
    }
  };

  const onEscape = useCallback(() => closeUploadDialog(), [closeUploadDialog]);

  useUploadEvents({
    onEscape,
  });

  return (
    <Box
      onDragStart={e => {
        // Only prevent default for non-DataGrid elements
        const target = e.target as HTMLElement;
        const isColumnHeader = target.closest(`.${gridClasses.columnHeader}`);

        if (!isColumnHeader) {
          // this prevents from dragging other elements on the page (e.g. links etc.)
          e.preventDefault();
        }
      }}
      onDragEnter={e => {
        // Try onDragEnter first - if it works, great
        const hasFiles = e.dataTransfer?.types?.includes('Files');
        const target = e.target as HTMLElement;
        const isColumnHeader = target.closest(`.${gridClasses.columnHeader}`);

        if (hasFiles && !isColumnHeader && uploadEnabled && !isDragging) {
          e.preventDefault();
          setIsDragging(true);
        }
      }}
      onDragOver={e => {
        // Only handle file drag events, not column reordering
        const hasFiles = e.dataTransfer?.types?.includes('Files');
        const target = e.target as HTMLElement;
        const isColumnHeader = target.closest(`.${gridClasses.columnHeader}`);

        if (hasFiles && !isColumnHeader && uploadEnabled) {
          e.preventDefault();
          // Only set isDragging if not already set (avoid continuous state updates)
          if (!isDragging) {
            setIsDragging(true);
          }
        }
      }}
      data-page-title={pageTitle}
      position="relative"
      flexGrow={1}
      height={1}
      sx={{ backgroundColor: 'inherit' }}
    >
      <Box
        component="input"
        disabled={!uploadEnabled || !canUploadToQueue}
        onDragLeave={() => setIsDragging(false)}
        onDrop={() => setIsDragging(false)}
        onClick={event => event.preventDefault()}
        width={isDragging ? 1 : 0}
        sx={{
          cursor: uploadEnabled && canUploadToQueue ? 'copy' : 'not-allowed',
          opacity: 0,
          zIndex: theme => theme.zIndex.tooltip,
          ...absoluteMaxSizeStyles,
        }}
        // changing key clears the file input
        key={`${isUploadDialogOpen}-1`}
        onChange={e => handleOnChange(e.target.files)}
        {...commonInputProps}
      />
      {isDragging ? (
        <DragOverlay canUploadToQueue={canUploadToQueue ?? true} />
      ) : null}
      <UploadDialog
        open={isUploadDialogOpen}
        onClose={closeUploadDialog}
        fileList={selectedFiles}
        removeFileFromList={filterFileFromState}
        currentQueue={selectedQueue}
        inbox={selectedQueue?.inbox}
        uploadInputProps={{
          ...commonInputProps,
          onChange: e => handleOnChange(e.target.files),
        }}
        dialogActions={
          <DialogActions sx={{ p: 3 }}>
            <Button
              variant="outlined"
              onClick={() => closeUploadDialog()}
              color="secondary"
              data-cy="upload-docs-cancel-btn"
            >
              {intl.formatMessage({
                id: 'containers.annotationList.upload.cancel',
              })}
            </Button>
            <Button
              variant="contained"
              type="submit"
              data-cy="upload-docs-confirm-queue-and-submit-btn"
              form="upload-additional-values-form"
              disabled={isUploading || hasFormErrors}
              startIcon={isUploading && <CircularProgress size={16} />}
            >
              {intl.formatMessage({
                id: 'containers.annotationList.upload.confirmAndUpload',
              })}
            </Button>
          </DialogActions>
        }
      >
        {selectedFiles.length ? (
          <ValuesFooter
            selectedQueue={selectedQueue}
            setSelectedQueue={queue => setSelectedQueueUrl(queue?.url)}
            workspacesWithQueues={workspacesWithQueues}
            onConfirm={values => onUploadConfirm(values)}
            onFormStateChange={setHasFormErrors}
          />
        ) : null}
      </UploadDialog>
      {typeof children === 'function'
        ? children({
            onUpload: files => handleOnChange(files),
          })
        : children}
    </Box>
  );
};
